import { AppointmentInfoProviderProps } from '../interfaces';
import {
  formatTimeZone,
  fromFhirAppointmentTypeToLocal,
  getZonedMoment,
} from 'utilities/functions';
import { appointmentRenderInfo } from 'app/my-appointments/interfaces';
import { Card } from '../../../app/my-appointments/components/Card';
import { ReactComponent as DollarIcon } from 'assets/icons/dollar.svg';
import { ReactComponent as Clock } from 'assets/icons/time.svg';
import { ReactComponent as Calendar } from 'assets/icons/calendar.svg';
import { ReactComponent as Duration } from 'assets/icons/appointmentsIcons/duration.svg';
import { GetPatientOutput, ProviderOutput } from 'graphql/generated/hasura';
import { ReactComponent as Info } from 'assets/icons/info-new.svg';

import React, { useMemo } from 'react';
import { AppointmentHasuraStatus } from '../../../app/my-appointments/const/appointment';
import { useAppointmentHasuraStatus } from '../../../app/my-appointments/components/upcoming-appointments/helper/getStatusClassName';
import { twMerge } from 'tailwind-merge';
import { Roles } from '../../../firebase/interfaces';
import { useAuth } from 'auth/context/AuthContext';
import { PatientInfo } from './PatientInfo';
import { ProviderInfo } from './ProviderInfo';
import CustomTooltip from 'components/CustomTooltip';
import StatusHistory from '../../../app/my-appointments/components/appointment-details/StatusHistory';
import moment from 'moment';

const formatDateForAppointment = (date: Date | string) => {
  const m = getZonedMoment(date);
  if (!m) return { day: '', time: '' };

  const day = m.format('dddd, MMM D, YYYY');
  const time = `${m.format('hh:mm A')} ${formatTimeZone(m)}`;

  return { day, time };
};

const AppointmentInfo: React.FC<AppointmentInfoProviderProps> = ({
  datetime,
  duration,
  appointmentType,
  locale,
  appointmentStatus,
  discount,
  appointmentPrice,
  patient = {} as GetPatientOutput,
  provider = {} as ProviderOutput,
  statusHistory = [],
}) => {
  const { user } = useAuth();
  const { day, time } = formatDateForAppointment(datetime);
  const { icon: TypeIcon, label: labelType } =
    appointmentRenderInfo[
      fromFhirAppointmentTypeToLocal(appointmentType || undefined)
    ];

  const isProvider = useMemo(() => user?.role === Roles.PROVIDER, [user]);

  const discountValue = useMemo(() => {
    const { amountOff, percentOff } = discount ?? {};
    return (
      (amountOff ?? 0) / 100 ||
      ((Number(appointmentPrice) || 0) * (percentOff || 0)) / 100
    );
  }, [discount, appointmentPrice]);

  const payed = useMemo(() => {
    return Math.max(Number(appointmentPrice || 0) - discountValue, 0);
  }, [discountValue, appointmentPrice]);

  const status = (appointmentStatus ||
    AppointmentHasuraStatus.Canceled) as AppointmentHasuraStatus;

  const {
    label,
    statusClassName,
    notification,
    notificationClassNames,
    iconClassNames,
    Icon,
  } = useAppointmentHasuraStatus(status);

  return (
    <>
      <Card
        title={locale?.aboutAppointment || locale?.aboutYourAppointment}
        titleClassName="flex text-h4 text-btn-black font-semibold"
        alwaysExpanded
        showSeparator
        testId="appointment-info"
      >
        {appointmentStatus && (
          <div className="w-full flex gap-1.5 items-center">
            <span
              className={twMerge(
                'capitalize text-clc-red border-clc-red bg-red-50',
                statusClassName,
              )}
              data-testid={'appointment-status'}
              data-value={AppointmentHasuraStatus[appointmentStatus]}
            >
              {label}
            </span>
            <span>
              <Info
                data-tooltip-id={`appointment-${appointmentStatus}`}
                className={twMerge('w-5 h-5 fill-charcoal-gray')}
              />
            </span>

            {notification && (
              <div className="flex items-start mt-2">
                {!!Icon && (
                  <Icon
                    data-tooltip-id={'notification-icon'}
                    className={iconClassNames}
                  />
                )}
                <div
                  className={twMerge(
                    'text-sm font-semibold',
                    notificationClassNames,
                  )}
                >
                  {notification}
                </div>
              </div>
            )}
          </div>
        )}
        <div className="flex w-full flex-col gap-2.5">
          <div
            className="flex w-full items-center"
            data-testid={`appointment-day-${datetime}`}
          >
            <Calendar className="h-5 w-5 fill-btn-black" />
            <div className="text-base font-medium text-left ml-2.5">{day}</div>
          </div>
          <div
            className="flex w-full items-center"
            data-testid={`appointment-time-${datetime}`}
          >
            <Clock className="h-5 w-5 fill-btn-black" />
            <div className="text-base font-medium text-left ml-2.5">{time}</div>
          </div>
          <div
            className="flex w-full items-center"
            data-testid={`appointment-duration-${duration}`}
          >
            <Duration className="h-5 w-5 fill-btn-black" />
            <div className="text-base font-medium text-left ml-2.5">
              {duration} min
            </div>
          </div>
          <div
            className="flex w-full items-center"
            data-testid={`appointment-type-${appointmentType}`}
          >
            <TypeIcon className="h-5 w-5 fill-btn-black" />
            <div className="text-base font-medium text-left ml-2.5">
              {labelType}
            </div>
          </div>

          <div className="flex w-full items-center">
            <DollarIcon className="h-5 w-5 fill-btn-black" />
            <div className="text-base font-medium text-left ml-2.5">
              {appointmentPrice &&
                `${locale.paidStatus} ($${
                  isProvider ? appointmentPrice : payed
                })`}
            </div>
          </div>
          <div
            className={twMerge(
              'w-full items-center hidden',
              !isProvider && discount && 'flex',
            )}
          >
            <div className="text-base ml-8 font-medium text-left">
              {appointmentPrice && `Price ($${appointmentPrice})`}
            </div>
          </div>
          <div
            className={twMerge(
              'w-full items-center hidden',
              !isProvider && discount && 'flex',
            )}
          >
            <div className="text-base ml-8 font-medium text-left">
              Discount{' '}
              <span className="text-bright-green">
                {discountValue && `(-$${discountValue})`}
              </span>
            </div>
          </div>
        </div>
      </Card>

      {isProvider ? (
        <PatientInfo locale={locale} patient={patient} />
      ) : (
        <ProviderInfo locale={locale} provider={provider} />
      )}

      <CustomTooltip
        id={`appointment-${appointmentStatus}`}
        place="bottom-end"
        classNameArrow="!bg-transparent"
        className="!bg-white !opacity-100 z-50 -translate-y-2 !border !border-gray-opacity-15 !rounded-10 !p-5"
      >
        <div className="flex flex-col cursor-auto gap-6">
          <p className="font-semibold relative flex items-center justify-between text-base text-neutral-800">
            <span>Status history</span>
          </p>
          <StatusHistory
            statusHistory={statusHistory.sort((a, b) =>
              moment(a.datetime).diff(moment(b.datetime)),
            )}
          />
        </div>
      </CustomTooltip>
    </>
  );
};

export default AppointmentInfo;
